import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable, Subject, combineLatest } from 'rxjs';
import {
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	startWith,
	takeUntil,
} from 'rxjs/operators';

import { Store } from '@ngrx/store';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { SnackBarService } from 'src/app/shared/components/snack-bar/snack-bar.service';

import { AppConstants } from '../../../../app.constants';
import { PartnerBroker } from '../../models/organizations.model';
import {
	deletePartnerBrokerStart,
	updatePartnerBrokerStatusStart,
	fetchPartnerBrokersStart,
} from '../../state/organizations.actions';
import {
	selectPartnerBrokers,
	selectOrgLoading,
} from '../../state/organizations.selector';

import { LinkOrganizationsModalComponent } from './link-organizations-modal/link-organizations-modal.component';

@Component({
	selector: 'app-partner-brokers',
	styleUrls: ['./partner-brokers.component.scss'],
	templateUrl: './partner-brokers.component.html',
})
export class PartnerBrokersComponent implements OnInit, OnDestroy {
	brokers$!: Observable<PartnerBroker[] | null>;
	isLoading$!: Observable<boolean>;
	organizationId!: string;
	searchForm: UntypedFormGroup;

	private readonly destroy$ = new Subject<void>();

	constructor(
		private readonly store: Store,
		private readonly dialog: MatDialog,
		private readonly snackBarService: SnackBarService,
		private readonly route: ActivatedRoute,
		private readonly fb: UntypedFormBuilder,
		private readonly router: Router,
	) {
		this.searchForm = this.fb.group({
			term: [''],
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngOnInit(): void {
		const id = this.route.snapshot.paramMap.get('id');

		if (id) {
			this.organizationId = id;
			this.store.dispatch(fetchPartnerBrokersStart());
		}

		const allBrokers$ = this.store.select(selectPartnerBrokers);
		this.isLoading$ = this.store.select(selectOrgLoading);

		const searchTerm$ = this.searchForm
			.get('term')!
			.valueChanges.pipe(
				startWith(''),
				debounceTime(AppConstants.debounce.time),
				distinctUntilChanged(),
			);

		this.brokers$ = combineLatest([allBrokers$, searchTerm$]).pipe(
			map(([brokers, term]) => {
				if (!brokers) {
					return null;
				}

				if (!term?.trim()) {
					return brokers;
				}

				const lowerTerm = term.toLowerCase();
				const cleanCnpjTerm = term.replace(/[^\d]/g, '');

				return brokers.filter(
					(broker: PartnerBroker) =>
						(broker.name || '').toLowerCase().includes(lowerTerm) ||
						(cleanCnpjTerm &&
							(broker.cnpj || '')
								.replace(/[^\d]/g, '')
								.includes(cleanCnpjTerm)),
				);
			}),
			takeUntil(this.destroy$),
		);
	}

	onActivate(broker: PartnerBroker): void {
		this.store.dispatch(
			updatePartnerBrokerStatusStart({ id: broker.id, active: true }),
		);
	}

	onDeactivate(broker: PartnerBroker): void {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			data: {
				message: `Tem certeza que deseja desativar a corretora "${broker.name}"?`,
				title: 'Confirmar Desativação',
			},
		});
		dialogRef
			.afterClosed()
			.pipe(filter((res) => res))
			.subscribe(() => {
				this.store.dispatch(
					updatePartnerBrokerStatusStart({ id: broker.id, active: false }),
				);
			});
	}

	onDelete(broker: PartnerBroker): void {
		if ((broker.linkedOrganizationsCount ?? 0) > 0) {
			this.snackBarService.showSnack(
				'Não é possível excluir uma corretora com organizações vinculadas.',
				'error',
			);

			return;
		}

		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			data: {
				confirmButtonText: 'Excluir',
				message: `Tem certeza que deseja excluir a corretora "${broker.name}"? Esta ação não pode ser desfeita.`,
				title: 'Confirmar Exclusão',
			},
		});
		dialogRef
			.afterClosed()
			.pipe(filter((res) => res))
			.subscribe(() => {
				this.store.dispatch(deletePartnerBrokerStart({ id: broker.id }));
			});
	}

	onEdit(broker: PartnerBroker): void {
		this.router.navigate([
			'/organizacoes',
			this.organizationId,
			'corretoras',
			broker.id,
		]);
	}

	onLink(broker: PartnerBroker): void {
		const dialogRef = this.dialog.open(LinkOrganizationsModalComponent, {
			data: { mode: 'link', partner: broker },
		});

		dialogRef
			.afterClosed()
			.pipe(filter((result) => !!result))
			.subscribe(() => {
				this.store.dispatch(fetchPartnerBrokersStart());
			});
	}

	onNewBroker(): void {
		this.router.navigate([
			'/organizacoes',
			this.organizationId,
			'corretoras',
			'cadastro',
		]);
	}

	onUnlink(broker: PartnerBroker): void {
		const dialogRef = this.dialog.open(LinkOrganizationsModalComponent, {
			data: { mode: 'unlink', partner: broker },
		});

		dialogRef
			.afterClosed()
			.pipe(filter((result) => !!result))
			.subscribe(() => {
				this.store.dispatch(fetchPartnerBrokersStart());
			});
	}

	// eslint-disable-next-line class-methods-use-this
	trackByBrokerId(index: number, broker: PartnerBroker): string {
		return broker.id;
	}
}
